/**
 * lib/portfolioOptimizer.ts — PyPortfolioOpt-equivalent in pure TypeScript.
 *
 * Implements:
 *   - Expected returns (geometric mean of log returns, annualised)
 *   - Sample covariance matrix (annualised)
 *   - Closed-form minimum-variance weights   (w = Σ⁻¹·𝟙 / (𝟙ᵀ·Σ⁻¹·𝟙))
 *   - Closed-form max-Sharpe weights         (w = Σ⁻¹·μ_ex / (𝟙ᵀ·Σ⁻¹·μ_ex))
 *   - Inverse-volatility risk-parity weights (w_i ∝ 1/σ_i)
 *
 * Constraints: long-only by clipping to ≥ 0 + renormalisation. For full
 * SLSQP-style constrained optimisation you'd want a dedicated solver,
 * but for indicative allocations the closed-form + clip approach gives
 * the same answer 80% of the time and is robust on small portfolios.
 *
 * No external deps. Matrix inversion via Gauss-Jordan elimination — fine
 * up to ~30 assets which is well beyond the watchlist size we feed it.
 */

const TRADING_DAYS = 252;

export interface OptimResult {
  symbols:           string[];
  weights:           number[];   // same order as `symbols`, sums to ~1
  expectedReturn:    number;     // annualised
  expectedVolatility:number;     // annualised
  sharpe:            number;
  // True when the optimisation could not be performed and these weights
  // are a fallback (equal-weight), not a solution. Callers MUST surface
  // this — silently returning fallback weights that look like an answer
  // is how a user allocates real capital on a computation that failed.
  degenerate?:       boolean;
  note?:             string;
}

export type OptimMethod = 'min_variance' | 'max_sharpe' | 'risk_parity';

/** Compute log returns from a price series. Drops the first observation. */
export function logReturns(prices: number[]): number[] {
  const r: number[] = [];
  for (let i = 1; i < prices.length; i++) {
    if (prices[i - 1] > 0 && prices[i] > 0) r.push(Math.log(prices[i] / prices[i - 1]));
  }
  return r;
}

/** Mean of an array. */
function mean(xs: number[]): number { return xs.reduce((s, x) => s + x, 0) / Math.max(1, xs.length); }

/** Sample covariance between two series of equal length. */
function cov(a: number[], b: number[]): number {
  const n = Math.min(a.length, b.length);
  if (n < 2) return 0;
  const ma = mean(a.slice(0, n)), mb = mean(b.slice(0, n));
  let s = 0;
  for (let i = 0; i < n; i++) s += (a[i] - ma) * (b[i] - mb);
  return s / (n - 1);
}

/**
 * Build the annualised covariance matrix from per-asset log-return series.
 * Trims each series to the shortest length so they're aligned.
 */
function covMatrix(returns: number[][]): number[][] {
  const minLen = Math.min(...returns.map(r => r.length));
  const aligned = returns.map(r => r.slice(-minLen));
  const n = aligned.length;
  const m: number[][] = [];
  for (let i = 0; i < n; i++) {
    m.push([]);
    for (let j = 0; j < n; j++) {
      m[i].push(cov(aligned[i], aligned[j]) * TRADING_DAYS);
    }
  }
  return m;
}

/** Gauss-Jordan matrix inverse. Throws if singular. */
function invert(M: number[][]): number[][] {
  const n = M.length;
  // Augment with identity.
  const A: number[][] = M.map((row, i) => [...row, ...Array(n).fill(0).map((_, j) => (i === j ? 1 : 0))]);
  // Forward elimination with partial pivoting.
  for (let i = 0; i < n; i++) {
    let pivotRow = i;
    let pivotVal = Math.abs(A[i][i]);
    for (let r = i + 1; r < n; r++) {
      if (Math.abs(A[r][i]) > pivotVal) { pivotRow = r; pivotVal = Math.abs(A[r][i]); }
    }
    if (pivotVal < 1e-12) throw new Error('Matrix is singular or near-singular');
    if (pivotRow !== i) { [A[i], A[pivotRow]] = [A[pivotRow], A[i]]; }
    const pv = A[i][i];
    for (let c = 0; c < 2 * n; c++) A[i][c] /= pv;
    for (let r = 0; r < n; r++) {
      if (r === i) continue;
      const factor = A[r][i];
      if (factor === 0) continue;
      for (let c = 0; c < 2 * n; c++) A[r][c] -= factor * A[i][c];
    }
  }
  return A.map(row => row.slice(n));
}

/** Matrix · vector. */
function mv(M: number[][], v: number[]): number[] {
  return M.map(row => row.reduce((s, x, i) => s + x * v[i], 0));
}

/** Dot product. */
function dot(a: number[], b: number[]): number {
  return a.reduce((s, x, i) => s + x * b[i], 0);
}

/** Clip to >= 0 and renormalise to sum to 1. */
function longOnly(w: number[]): number[] {
  const clipped = w.map(x => Math.max(0, x));
  const s = clipped.reduce((acc, x) => acc + x, 0);
  return s > 0 ? clipped.map(x => x / s) : clipped.map(() => 1 / clipped.length);
}

/** Annualised expected return per asset (geometric mean of log returns × 252). */
function expectedReturns(returns: number[][]): number[] {
  return returns.map(r => (r.length === 0 ? 0 : mean(r) * TRADING_DAYS));
}

/** Per-asset annualised volatility. */
function vols(cov: number[][]): number[] {
  return cov.map((row, i) => Math.sqrt(Math.max(0, row[i])));
}

/**
 * Optimise a portfolio. `returns[i]` is the log-return series for symbol[i].
 * `riskFreeRate` defaults to 4.5% (current US 3-mo T-bill ballpark).
 */
export function optimize(
  symbols: string[],
  returns: number[][],
  method: OptimMethod,
  riskFreeRate = 0.045,
): OptimResult {
  const mu  = expectedReturns(returns);
  const cov = covMatrix(returns);
  const n   = symbols.length;

  let weights: number[];

  if (method === 'risk_parity') {
    // NOTE ON THE NAME: this is INVERSE-VOLATILITY weighting, which only
    // coincides with true risk parity when every pairwise correlation is
    // identical. True risk parity equalises each asset's marginal
    // contribution to portfolio variance and needs an iterative solve.
    // The label promises more than the math delivers — kept for API
    // compatibility, but the UI now says "inverse vol".
    const sigmas = vols(cov);
    const invSig = sigmas.map(s => s > 0 ? 1 / s : 0);
    const sum    = invSig.reduce((a, b) => a + b, 0);
    weights = sum > 0 ? invSig.map(x => x / sum) : Array(n).fill(1 / n);
  } else {
    // Both min-variance and max-Sharpe need Σ⁻¹.
    let invCov: number[][];
    try {
      invCov = invert(cov);
    } catch {
      // Singular covariance (duplicate symbols, or fewer observations
      // than assets). Equal-weight is a reasonable fallback, but the
      // STATS must not be reported as 0/0/0 — the UI rendered those as
      // "EXP RET 0.00% / EXP VOL 0.00% / SHARPE 0.00", indistinguishable
      // from a real optimisation that happened to be terrible. Compute
      // the honest stats for the equal-weight portfolio and flag it.
      const ew = Array(n).fill(1 / n);
      const ewRet = dot(ew, mu);
      const ewVar = dot(ew, mv(cov, ew));
      const ewVol = ewVar > 0 ? Math.sqrt(ewVar) : NaN;
      return {
        symbols, weights: ew,
        expectedReturn: ewRet,
        expectedVolatility: ewVol,
        sharpe: Number.isFinite(ewVol) && ewVol > 0 ? (ewRet - riskFreeRate) / ewVol : NaN,
        degenerate: true,
        note: 'Covariance matrix is singular (duplicate symbols or too few observations). Showing equal weights, not an optimisation.',
      };
    }

    if (method === 'min_variance') {
      const ones = Array(n).fill(1);
      const v    = mv(invCov, ones);
      const s    = ones.reduce((acc, x, i) => acc + x * v[i], 0);
      weights = s !== 0 ? v.map(x => x / s) : Array(n).fill(1 / n);
    } else {
      // max_sharpe: w ∝ Σ⁻¹ · (μ - rf·𝟙)
      const muEx = mu.map(m => m - riskFreeRate);
      const v    = mv(invCov, muEx);
      const ones = Array(n).fill(1);
      const s    = ones.reduce((acc, x, i) => acc + x * v[i], 0);
      // SIGN FLIP GUARD. When 𝟙ᵀΣ⁻¹μ_ex < 0 — which happens whenever the
      // excess returns are net negative — dividing by s inverts the sign
      // of every weight. longOnly() then clipped the negatives and kept
      // precisely the WRONG assets, and because the reported Sharpe was
      // recomputed from those mangled weights it looked internally
      // consistent. There is no long-only tangency portfolio when no asset
      // beats the risk-free rate; say so instead of returning nonsense.
      if (s <= 0) {
        const ew = Array(n).fill(1 / n);
        const ewRet = dot(ew, mu);
        const ewVar = dot(ew, mv(cov, ew));
        const ewVol = ewVar > 0 ? Math.sqrt(ewVar) : NaN;
        return {
          symbols, weights: ew,
          expectedReturn: ewRet,
          expectedVolatility: ewVol,
          sharpe: Number.isFinite(ewVol) && ewVol > 0 ? (ewRet - riskFreeRate) / ewVol : NaN,
          degenerate: true,
          note: 'No long-only maximum-Sharpe portfolio exists for this set: no asset’s expected return exceeds the risk-free rate over the window. Showing equal weights.',
        };
      }
      weights = v.map(x => x / s);
    }
    weights = longOnly(weights);
  }

  // Portfolio expected return + vol + Sharpe under these weights.
  const portRet = dot(weights, mu);
  const portVar = dot(weights, mv(cov, weights));
  const portVol = Math.sqrt(Math.max(0, portVar));
  const sharpe  = portVol > 0 ? (portRet - riskFreeRate) / portVol : 0;

  return {
    symbols, weights,
    expectedReturn:     portRet,
    expectedVolatility: portVol,
    sharpe,
  };
}
